import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleXmark, faPause, faPlay, faShuffle } from '@fortawesome/free-solid-svg-icons'
import cx from 'classnames'
import React, { ChangeEvent, createRef, useCallback, useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { THUMBNAIL_URL_BASE } from './constants'
import { TextInputWithLabel } from './InputWithLabel'
import UserContext, { UserContextType } from './UserContext'

export type GalleryItem = {
  type: string
  name: string
  isBlacklisted?: boolean
}

type Props = {
  displayedWorks: GalleryItem[]
  isPlaying: boolean
  searchQuery: string
  setSearchQuery: (query: string) => void
  togglePlay: () => void
  doShuffle: () => void
}
const Gallery = ({
  displayedWorks,
  searchQuery,
  isPlaying,
  togglePlay,
  doShuffle,
  setSearchQuery,
}: Props) => {
  const {
    userContext: { isFullscreen },
  } = useContext(UserContext) as UserContextType
  const searchBoxRef = createRef<HTMLInputElement>()
  const searchBarRef = React.createRef<HTMLDivElement>()
  const [isSearchBarVisible, setIsSearchBarVisible] = useState(true)
  const playText = isPlaying ? 'Pause' : 'Play'
  const playIcon = <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} title={playText} />
  const onMouseEnter = useCallback(() => {
    if (!isSearchBarVisible) {
      setIsSearchBarVisible(true)
    }
  }, [isSearchBarVisible])
  const onMouseLeave = useCallback(() => {
    if (isFullscreen && isSearchBarVisible && searchQuery.length) {
      setTimeout(() => {
        setIsSearchBarVisible(false)
      }, 5000)
    }
  }, [isSearchBarVisible, searchQuery, isFullscreen])
  useEffect(() => {
    const searchBarElem = searchBarRef.current
    if (searchBarElem) {
      searchBarElem.addEventListener('mouseenter', onMouseEnter)
      searchBarElem.addEventListener('mouseleave', onMouseLeave)

      return () => {
        searchBarElem.removeEventListener('mouseenter', onMouseEnter)
        searchBarElem.removeEventListener('mouseleave', onMouseLeave)
      }
    }
  }, [searchBarRef, onMouseLeave, onMouseEnter])

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }
  const addWork = (work: GalleryItem, index: number) => {
    const path = `${work.type}/${work.name}`
    const thumbUrl = `${THUMBNAIL_URL_BASE}/${path}x150.webp`
    return (
      <Link key={index} className="thumb" to={`archive/${path}`}>
        <img src={thumbUrl} width="75" alt="" />
        <div>{work.name}</div>
      </Link>
    )
  }
  const onClear = () => {
    setSearchQuery('')
    if (searchBoxRef.current) {
      searchBoxRef.current.value = ''
    }
  }
  const className = cx('searchBar', { hide: !isSearchBarVisible })

  return (
    <div className="gallery">
      <div className="search">
        <div className={className} ref={searchBarRef}>
          <div>
            <TextInputWithLabel
              id="searchBar1"
              label=""
              placeholder="search images..."
              value={searchQuery}
              onChange={onChange}
              onRef={searchBoxRef}
            />
            <button onClick={onClear} className="clear">
              <FontAwesomeIcon size="sm" icon={faCircleXmark} title="Clear" />
            </button>
          </div>
          <div>
            <button onClick={doShuffle} disabled={displayedWorks.length < 1}>
              <FontAwesomeIcon icon={faShuffle} title="Shuffle" />
            </button>
            <button
              id="play"
              aria-label={playText}
              onClick={togglePlay}
              disabled={displayedWorks.length < 1}
            >
              {playIcon}
            </button>
          </div>
        </div>
        <div className="searchResults">{displayedWorks.map(addWork)}</div>
      </div>
    </div>
  )
}

export default Gallery
